import React, { useEffect, useState } from 'react';
import { Building2, Plus, Copy } from 'lucide-react';
import { getCustomers, createCustomer, suspendCustomer, reactivateCustomer, resendCustomerInvite, setCustomerAiLimit, setCustomerBillingFree } from '../api';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import InviteLinkDialog, { InviteLinkField } from './InviteLink';
import SignupRequests from './SignupRequests';

// Settings > Customers: the platform owner's list of customer companies.
// Each customer has its own database. The original workspace (is_primary)
// is the owner's own and cannot be paused. Pausing a customer signs out
// everyone in it and blocks them until it is resumed.

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

// By default the first admin is emailed an invite and picks their own
// password. Unticked, the server makes a temporary password instead.
const EMPTY_FORM = { name: '', slug: '', admin_name: '', admin_email: '', send_invite: true };

// Lowercase, hyphenated, at most 40 characters, no hyphen at either end.
export const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');

const tokens = (n) => Number(n || 0).toLocaleString();

// "12,400 of 2,000,000", or "No cap", for this month's AI use.
export const allowanceText = (c) => (c.ai_monthly_token_limit == null
  ? `${tokens(c.ai_tokens_this_month)} · no cap`
  : `${tokens(c.ai_tokens_this_month)} of ${tokens(c.ai_monthly_token_limit)}`);

const when = (iso) => {
  if (!iso) return '';
  const text = String(iso);
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : text.replace(' ', 'T') + 'Z');
  return Number.isNaN(date.getTime()) ? text : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// A short billing label for the owner's list, and its colour.
export const billingLabel = (c) => {
  const b = c.billing;
  if (c.is_primary || !b || b.state === 'exempt') return { text: 'Not billed', tone: 'muted' };
  if (b.free || b.state === 'free') return { text: 'Free', tone: 'success' };
  if (b.locked || b.state === 'locked') return { text: 'Locked', tone: 'danger' };
  if (b.state === 'past_due') return { text: 'Payment failed', tone: 'warning' };
  if (b.state === 'trial') {
    const ends = b.trial_ends_at ? new Date(b.trial_ends_at) : null;
    return {
      text: ends && !Number.isNaN(ends.getTime())
        ? `Trial to ${ends.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
        : 'Trial',
      tone: 'default',
    };
  }
  if (b.state === 'active') {
    const seats = b.billed_seats;
    return { text: seats == null ? 'Paying' : `Paying, ${seats} ${Number(seats) === 1 ? 'user' : 'users'}`, tone: 'success' };
  }
  return { text: 'Not subscribed', tone: 'muted' };
};

const BILLING_COLOURS = {
  muted: 'var(--text-muted)',
  success: 'var(--success, #16a34a)',
  warning: 'var(--warning, #d97706)',
  danger: 'var(--danger, #dc2626)',
};

export default function CustomersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugEdited, setSlugEdited] = useState(false);
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);

  // The new admin's sign-in details (or their invite), shown once right after creation.
  const [created, setCreated] = useState(null);
  const [copyNote, setCopyNote] = useState('');

  // Resending a first admin's invite: a confirmation, or the link to copy
  // when email is not set up on the server.
  const [notice, setNotice] = useState('');
  const [inviteLink, setInviteLink] = useState(null);

  // The monthly AI allowance being edited: { customer, value, noCap }.
  const [limitEdit, setLimitEdit] = useState(null);
  const [limitError, setLimitError] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);

  const [pauseTarget, setPauseTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getCustomers()
      .then(res => { if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || 'Failed to load customers'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const openForm = () => {
    setForm(EMPTY_FORM);
    setSlugEdited(false);
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    if (creating) return;
    setShowForm(false);
  };

  const onNameChange = (value) => {
    setForm(f => ({ ...f, name: value, slug: slugEdited ? f.slug : slugify(value) }));
  };

  const onSlugChange = (value) => {
    setSlugEdited(true);
    setForm(f => ({ ...f, slug: value.toLowerCase() }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError('');
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      admin_name: form.admin_name.trim(),
      admin_email: form.admin_email.trim(),
    };
    if (form.send_invite) payload.send_invite = true;
    if (!payload.name || !payload.slug || !payload.admin_name || !payload.admin_email) {
      setFormError('Fill in every field.');
      return;
    }
    if (!SLUG_PATTERN.test(payload.slug)) {
      setFormError('The customer ID can use 1 to 40 lowercase letters, digits or hyphens, and cannot start or end with a hyphen.');
      return;
    }
    setCreating(true);
    try {
      const res = await createCustomer(payload);
      const { customer, admin } = res.data || {};
      if (customer) setRows(list => [...list.filter(r => r.id !== customer.id), customer]);
      setShowForm(false);
      setForm(EMPTY_FORM);
      setCopyNote('');
      setNotice('');
      setCreated({ customerName: customer?.name || payload.name, name: payload.admin_name, email: payload.admin_email, ...admin });
    } catch (err) {
      setFormError(err.response?.data?.error || 'Could not create that customer');
    } finally {
      setCreating(false);
    }
  };

  const copyCredentials = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(`Email: ${created.email}\nTemporary password: ${created.temporary_password}`);
      setCopyNote('Copied to the clipboard.');
    } catch (err) {
      setCopyNote('Could not copy. Select the text and copy it instead.');
    }
  };

  const closeCreated = () => {
    setCreated(null);
    setCopyNote('');
  };

  const resendInvite = async (customer) => {
    setBusyId(customer.id);
    setError('');
    setNotice('');
    try {
      const res = await resendCustomerInvite(customer.id);
      const admin = res.data?.admin || {};
      const invite = admin.invite || {};
      if (invite.url) {
        setInviteLink({ url: invite.url, name: admin.name, email: admin.email, kind: 'invite' });
      } else if (invite.sent) {
        setNotice(`Invite sent to ${admin.email || `the first admin of ${customer.name}`}.`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resend that invite');
    } finally {
      setBusyId(null);
    }
  };

  const changeStatus = async (customer, action) => {
    setBusyId(customer.id);
    setError('');
    try {
      const res = await (action === 'suspend' ? suspendCustomer(customer.id) : reactivateCustomer(customer.id));
      const updated = res.data?.customer;
      setRows(list => list.map(r => (r.id === customer.id
        ? (updated || { ...r, status: action === 'suspend' ? 'suspended' : 'active' })
        : r)));
    } catch (err) {
      setError(err.response?.data?.error || (action === 'suspend' ? 'Could not pause that customer' : 'Could not resume that customer'));
    } finally {
      setBusyId(null);
    }
  };

  const onSignupApproved = (customer, admin, request) => {
    if (customer) setRows(list => [...list.filter(r => r.id !== customer.id), customer]);
    setNotice('');
    setCreated({ customerName: customer?.name || request.company, name: admin?.name || request.name, email: admin?.email || request.email, ...admin });
  };

  const openLimit = (customer) => {
    setLimitError('');
    setLimitEdit({
      customer,
      noCap: customer.ai_monthly_token_limit == null,
      value: customer.ai_monthly_token_limit == null ? '' : String(customer.ai_monthly_token_limit),
    });
  };

  const saveLimit = async (e) => {
    e.preventDefault();
    const raw = String(limitEdit.value).replace(/[,\s]/g, '');
    let value = null;
    if (!limitEdit.noCap) {
      if (!/^\d+$/.test(raw)) {
        setLimitError('Enter a whole number of tokens, or tick No cap.');
        return;
      }
      value = Number(raw);
    }
    setSavingLimit(true);
    setLimitError('');
    try {
      const res = await setCustomerAiLimit(limitEdit.customer.id, value);
      const updated = res.data?.customer;
      setRows(list => list.map(r => (r.id === limitEdit.customer.id ? (updated || { ...r, ai_monthly_token_limit: value }) : r)));
      setLimitEdit(null);
    } catch (err) {
      setLimitError(err.response?.data?.error || 'Could not change the allowance');
    } finally {
      setSavingLimit(false);
    }
  };

  const toggleFree = async (customer) => {
    const free = !customer.billing?.free;
    setBusyId(customer.id);
    setError('');
    try {
      const res = await setCustomerBillingFree(customer.id, free);
      const updated = res.data?.customer;
      setRows(list => list.map(r => (r.id === customer.id
        ? (updated || { ...r, billing: { ...r.billing, free, state: free ? 'free' : r.billing?.state, locked: free ? false : r.billing?.locked } })
        : r)));
    } catch (err) {
      setError(err.response?.data?.error || (free ? 'Could not put that customer on the free plan' : 'Could not take that customer off the free plan'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmPause = async () => {
    const target = pauseTarget;
    setPauseTarget(null);
    if (target) await changeStatus(target, 'suspend');
  };

  return (
    <>
    <SignupRequests onApproved={onSignupApproved} />
    <div className="card">
      <div className="card-header" style={{ gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h3 className="card-title">Customers</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Each customer is a company with its own workspace and database.
          </p>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={openForm}>
          <Plus size={16} /> New customer
        </button>
      </div>
      {error && (
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="alert alert-error" role="alert">{error}</div>
        </div>
      )}
      {notice && (
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="alert alert-success" role="status">{notice}</div>
        </div>
      )}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Customer</th>
              <th scope="col">Users</th>
              <th scope="col">Created</th>
              <th scope="col">AI tokens this month</th>
              <th scope="col">Billing</th>
              <th scope="col">Status</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7"><div className="loading-container"><div className="spinner"></div></div></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7"><div className="empty-state"><Building2 size={48} className="empty-state-icon" /><h3 className="empty-state-title">No customers yet</h3></div></td></tr>
            ) : (
              rows.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{c.slug}</div>
                  </td>
                  <td>{c.user_count ?? 0}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{when(c.created_at)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ color: c.ai_monthly_token_limit != null && Number(c.ai_tokens_this_month) >= Number(c.ai_monthly_token_limit) ? 'var(--danger, #dc2626)' : undefined }}>
                      {allowanceText(c)}
                    </span>
                    {!c.is_primary && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: '6px' }}
                        onClick={() => openLimit(c)}
                        aria-label={`Change the AI allowance for ${c.name}`}
                      >
                        Change
                      </button>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const label = billingLabel(c);
                      return (
                        <span style={{ color: BILLING_COLOURS[label.tone], fontWeight: label.tone === 'warning' || label.tone === 'danger' ? 600 : undefined }}>
                          {label.text}
                        </span>
                      );
                    })()}
                    {!c.is_primary && c.billing && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: '6px' }}
                        disabled={busyId === c.id}
                        onClick={() => toggleFree(c)}
                        aria-label={c.billing.free ? `Remove free plan from ${c.name}` : `Mark ${c.name} free`}
                        title={c.billing.free ? 'Start billing this customer again' : 'Stop billing this customer. A locked workspace opens again.'}
                      >
                        {c.billing.free ? 'Remove free' : 'Mark free'}
                      </button>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${c.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                      {c.status === 'active' ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.is_primary ? (
                      <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Your workspace</span>
                    ) : c.status === 'active' ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {c.admin_invite_pending && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={busyId === c.id}
                            onClick={() => resendInvite(c)}
                            aria-label={`Resend invite to the first admin of ${c.name}`}
                            title="Send the first admin's set-password invite again, while they have not set a password"
                          >
                            Resend invite
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={busyId === c.id}
                          onClick={() => setPauseTarget(c)}
                          aria-label={`Pause ${c.name}`}
                        >
                          Pause
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={busyId === c.id}
                        onClick={() => changeStatus(c, 'reactivate')}
                        aria-label={`Resume ${c.name}`}
                      >
                        Resume
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showForm} onClose={closeForm} title="New customer">
        <form onSubmit={submit} noValidate>
          <div className="modal-body">
            {formError && <div className="alert alert-error" role="alert">{formError}</div>}
            <div className="form-group">
              <label className="form-label" htmlFor="customer-name">Company name *</label>
              <input id="customer-name" type="text" className="form-input" value={form.name} onChange={(e) => onNameChange(e.target.value)} required autoComplete="off" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="customer-slug">Customer ID *</label>
              <input id="customer-slug" type="text" className="form-input" value={form.slug} onChange={(e) => onSlugChange(e.target.value)} required maxLength={40} autoComplete="off" aria-describedby="customer-slug-hint" />
              <p id="customer-slug-hint" style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                Lowercase letters, digits and hyphens. This cannot be changed later.
              </p>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="customer-admin-name">First admin's name *</label>
              <input id="customer-admin-name" type="text" className="form-input" value={form.admin_name} onChange={(e) => setForm(f => ({ ...f, admin_name: e.target.value }))} required autoComplete="off" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="customer-admin-email">First admin's email *</label>
              <input id="customer-admin-email" type="email" className="form-input" value={form.admin_email} onChange={(e) => setForm(f => ({ ...f, admin_email: e.target.value }))} required autoComplete="off" />
            </div>
            <div className="form-group">
              <label className="checkbox-wrapper">
                <input type="checkbox" className="checkbox" checked={form.send_invite} onChange={(e) => setForm(f => ({ ...f, send_invite: e.target.checked }))} />
                Email an invite to the first admin
              </label>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                {form.send_invite
                  ? 'They get a link to choose their own password.'
                  : 'You get a temporary password to share with them yourself.'}
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeForm} disabled={creating}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create customer'}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!created} onClose={closeCreated} title="Customer created">
        {created && created.invite ? (
          <>
            <div className="modal-body">
              {created.invite.url ? (
                <>
                  <p style={{ marginTop: 0 }}>{created.customerName} is ready.</p>
                  <div className="alert alert-warning">
                    Email isn't set up on this server yet, so no invite was sent.
                  </div>
                  <p>
                    Send this link to {created.name || 'the first admin'}{created.email ? ` (${created.email})` : ''} yourself.
                    It lets them set a password and sign in. It works once and expires after a while.
                  </p>
                  <InviteLinkField url={created.invite.url} id="customer-invite-link" />
                </>
              ) : (
                <p role="status" style={{ margin: 0 }}>
                  {created.customerName} is ready. We emailed an invite to {created.email || 'the first admin'}.
                  They choose their own password from the link in it.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={closeCreated}>Done</button>
            </div>
          </>
        ) : created && (
          <>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                {created.customerName} is ready. Share these sign-in details with {created.name || 'the first admin'}.
              </p>
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', margin: '0 0 16px' }}>
                <dt style={{ color: 'var(--text-secondary)' }}>Email</dt>
                <dd style={{ margin: 0, fontWeight: 600 }}>{created.email}</dd>
                <dt style={{ color: 'var(--text-secondary)' }}>Temporary password</dt>
                <dd style={{ margin: 0, fontWeight: 600, fontFamily: 'monospace', userSelect: 'all' }}>{created.temporary_password}</dd>
              </dl>
              <div className="alert alert-warning" style={{ marginBottom: copyNote ? '12px' : 0 }}>
                They must change this password the first time they sign in. It will not be shown again.
              </div>
              {copyNote && <p role="status" style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>{copyNote}</p>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={copyCredentials}><Copy size={16} /> Copy</button>
              <button type="button" className="btn btn-primary" onClick={closeCreated}>Done</button>
            </div>
          </>
        )}
      </Modal>

      <Modal isOpen={!!limitEdit} onClose={() => { if (!savingLimit) setLimitEdit(null); }} title="Monthly AI allowance">
        {limitEdit && (
          <form onSubmit={saveLimit} noValidate>
            <div className="modal-body">
              {limitError && <div className="alert alert-error" role="alert">{limitError}</div>}
              <p style={{ marginTop: 0 }}>
                How many AI tokens (input plus output) everyone at {limitEdit.customer.name} can use each calendar month.
                Once it's used up, AI features stop for them until the 1st. They have used {tokens(limitEdit.customer.ai_tokens_this_month)} this month.
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="ai-limit-tokens">Tokens per month</label>
                <input
                  id="ai-limit-tokens"
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  value={limitEdit.value}
                  disabled={limitEdit.noCap}
                  onChange={(e) => setLimitEdit(l => ({ ...l, value: e.target.value }))}
                  autoComplete="off"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="checkbox-wrapper">
                  <input type="checkbox" className="checkbox" checked={limitEdit.noCap} onChange={(e) => setLimitEdit(l => ({ ...l, noCap: e.target.checked }))} />
                  No cap
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setLimitEdit(null)} disabled={savingLimit}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={savingLimit}>{savingLimit ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        )}
      </Modal>

      <InviteLinkDialog result={inviteLink} onClose={() => setInviteLink(null)} />

      <ConfirmDialog
        isOpen={!!pauseTarget}
        onClose={() => setPauseTarget(null)}
        onConfirm={confirmPause}
        title="Pause customer"
        message={pauseTarget ? `Pause ${pauseTarget.name}? Everyone in this customer is signed out and blocked until you resume it.` : ''}
        confirmText="Pause"
      />
    </div>
    </>
  );
}
